import {
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Theme,
    Tooltip,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import SportsMotorsportsIcon from '@mui/icons-material/SportsMotorsports';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ISimplifiedEvent } from "../../../models/IEvent";
import { genericSort, getComparator } from '../../../services/SortingHelper';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import EnhancedTableToolbar from '../../common/EnhancedTableToolbar';
import ConfirmDialog from '../../common/ConfirmDialog';
import EventServiceInstance from '../../../services/EventService';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
        table: {
            minWidth: 750,
        },
        visuallyHidden: {
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            top: 20,
            width: 1,
        },
    }),
);

type Order = 'asc' | 'desc';

interface HeadCell {
    disablePadding: boolean;
    id: keyof ISimplifiedEvent;
    label: string;
    numeric: boolean;
    priority: number;
    align?: 'left' | 'right' | 'center';
}

interface IHeadTableCellProps {
    headCell: HeadCell;
    classes: ReturnType<typeof useStyles>;
    order: Order;
    orderBy: string;
    createSortHandler: (property: keyof ISimplifiedEvent) => (event: React.MouseEvent<unknown>) => void;
}

const showFromMdTableCellSx = { display: { xs: 'none', md: 'table-cell' } } as const;
const showFromLgTableCellSx = { display: { xs: 'none', lg: 'table-cell' } } as const;

const raceTypeIconMap: Record<string, { icon: JSX.Element; labelKey: string }> = {
    '1': { icon: <TwoWheelerIcon fontSize="small" />, labelKey: 'eventsetup.typebike' },
    '2': { icon: <DriveEtaIcon fontSize="small" />, labelKey: 'eventsetup.typecar' },
    '3': { icon: <SportsMotorsportsIcon fontSize="small" />, labelKey: 'eventsetup.typekart' },
};

function getPriorityCellSx(priority: number) {
    switch (priority) {
        case 2:
            return showFromMdTableCellSx;
        case 3:
            return showFromLgTableCellSx;
        default:
            return undefined;
    }
}

function HeadTableCell(props: IHeadTableCellProps) {
    const { classes, order, orderBy, createSortHandler, headCell } = props;
    const { t } = useTranslation("common");
    const align = headCell.align ?? (headCell.numeric ? 'right' : 'left');

    return (
        <TableCell
            key={headCell.id}
            align={align}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={getPriorityCellSx(headCell.priority)}
        >
            <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                sx={align === 'right' ? { flexDirection: 'row-reverse' } : undefined}
            >
                {t(headCell.label)}
                {orderBy === headCell.id ? (
                    <span className={classes.visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </span>
                ) : null}
            </TableSortLabel>
        </TableCell>);
}

interface EnhancedTableProps {
    classes: ReturnType<typeof useStyles>;
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof ISimplifiedEvent) => void;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
}


const headCells: HeadCell[] = [
    { id: 'name', numeric: false, disablePadding: true, label: 'eventsTable.name', priority: 1 },
    { id: 'description', numeric: false, disablePadding: true, label: 'eventsTable.description', priority: 2 },
    { id: 'eventDate', numeric: true, disablePadding: true, label: 'eventsTable.eventDate', priority: 1 },
    { id: 'racePrivate', numeric: false, disablePadding: true, label: 'eventsTable.racePrivate', priority: 3, align: 'center' },
    { id: 'raceTypeId', numeric: false, disablePadding: true, label: 'eventsTable.raceTypeId', priority: 3, align: 'center' }
];

function EnhancedTableHead(props: EnhancedTableProps) {
    const { classes, order, orderBy, onRequestSort } = props;
    const createSortHandler = (property: keyof ISimplifiedEvent) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };
    const { t } = useTranslation("common");

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) =>
                    (<HeadTableCell key={headCell.id} headCell={headCell} orderBy={orderBy} order={order} classes={classes} createSortHandler={createSortHandler}></HeadTableCell>)
                )}
                <TableCell align="center">{t('eventsTable.actions')}</TableCell>
            </TableRow>
        </TableHead >
    );
}

interface IEventListProps {
    events: ISimplifiedEvent[];
}

function OrganizerEventsTable(props: IEventListProps) {
    const classes = useStyles();
    const theme = useTheme();
    const [order, setOrder] = React.useState<Order>('desc');
    const [orderBy, setOrderBy] = React.useState<keyof ISimplifiedEvent | ''>('');
    const [selected, setSelected] = React.useState<string[]>([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(-1);
    const [orgEvents, setOrgEvents] = React.useState<ISimplifiedEvent[]>([]);
    const [confirmAction, setConfirmAction] = useState<{ type: 'start' | 'stop'; eventId: string; eventName: string } | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation("common");
    const { enqueueSnackbar } = useSnackbar();

    // useEffect(() => {
    //     if (orgEvents.length === 0 && props.events.length > 0) {
    //         setOrgEvents(props.events);
    //     }
    // }, [props.events, orgEvents.length, orgEvents]);
    useEffect(() => {
        setOrgEvents(props.events);
    }, [props.events]);



    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof ISimplifiedEvent) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelecteds = orgEvents.map((r) => r.raceId);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleClick = (event: React.MouseEvent<unknown>, eventId: string) => {
        navigate(`/event/setup/${eventId}`);
    };

    const isSelected = (tagId: string) => selected.indexOf(tagId) !== -1;

    const emptyRows = rowsPerPage > 0 ? rowsPerPage - Math.min(rowsPerPage, orgEvents.length - page * rowsPerPage) : 0;

    const sortedEvents: ISimplifiedEvent[] = orderBy
        ? genericSort(orgEvents, getComparator(order, orderBy))
        : orgEvents;

    const eventFilterChanged = (event: any) => {
        const filterValue: string = event.target.value.toLowerCase();
        if (filterValue.length > 0) {
            const filteredEvents = props.events.filter(r => (r.name ?? '').toLowerCase().indexOf(filterValue) >= 0
                || (r.circuitName ?? '').toLowerCase().indexOf(filterValue) >= 0
                || (r.description ?? '').toLowerCase().indexOf(filterValue) >= 0);
            setOrgEvents(filteredEvents);
        } else {
            setOrgEvents(props.events);
        }
    }

    const getRowSx = (status: number) => {
        if (status === 1) return { backgroundColor: 'success.light' };
        if (status === 0) return { backgroundColor: alpha(theme.palette.success.dark, 0.25) };
        return {};
    };

    const handleConfirmAction = async () => {
        if (!confirmAction) return;
        const { type, eventId } = confirmAction;
        setConfirmAction(null);
        try {
            if (type === 'start') {
                await EventServiceInstance.startEvent(eventId);
                setOrgEvents(prev => prev.map(e => e.raceId === eventId ? { ...e, status: 1 } : e));
            } else {
                await EventServiceInstance.stopEvent(eventId);
                setOrgEvents(prev => prev.map(e => e.raceId === eventId ? { ...e, status: 2 } : e));
            }
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t(type === 'start' ? 'eventsetup.startError' : 'eventsetup.stopError'), { variant: 'error' });
        }
    };

    return (
        // <div className={classes.root}>
        // </div>
        <Paper className={classes.paper}>
            <ConfirmDialog
                open={confirmAction !== null}
                title={confirmAction?.type === 'start' ? t('eventsetup.startConfirmTitle') : t('eventsetup.stopConfirmTitle')}
                message={confirmAction?.type === 'start' ? t('eventsetup.startConfirmMessage') : t('eventsetup.stopConfirmMessage')}
                onAccept={handleConfirmAction}
                onCancel={() => setConfirmAction(null)}
            />
            <EnhancedTableToolbar itemsFilterChanged={eventFilterChanged} title={t('events.list')} />
            <TableContainer>
                <Table
                    className={classes.table}
                    aria-labelledby="tableTitle"
                    size={'small'}
                    aria-label="enhanced table"
                    stickyHeader
                >
                    <EnhancedTableHead
                        classes={classes}
                        order={order}
                        orderBy={orderBy}
                        onSelectAllClick={handleSelectAllClick}
                        onRequestSort={handleRequestSort}
                        rowCount={orgEvents.length}
                    />
                    <TableBody>
                        {sortedEvents
                            .slice(rowsPerPage > 0 ? page * rowsPerPage : 0, rowsPerPage > 0 ? page * rowsPerPage + rowsPerPage : undefined)
                            .map((row, index) => {
                                const isItemSelected = isSelected(row.raceId.toString());

                                return (
                                    <TableRow
                                        hover
                                        onClick={(event) => handleClick(event, row.raceId.toString())}
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.raceId.toString()}
                                        selected={isItemSelected}
                                        sx={getRowSx(row.status)}
                                    >
                                        <TableCell align="left">{row.name}</TableCell>
                                        <TableCell align="left" sx={showFromMdTableCellSx}>{row.description}</TableCell>
                                        <TableCell align="right">{new Date(row.eventDate).toLocaleDateString()}</TableCell>
                                        <TableCell align="center" sx={showFromLgTableCellSx}>
                                            {row.racePrivate ? (
                                                <Tooltip title={t('eventsTable.racePrivate')}>
                                                    <LockIcon fontSize="small" />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title={t('eventsTable.racePublic')}>
                                                    <LockOpenIcon fontSize="small" />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell align="center" sx={showFromLgTableCellSx}>
                                            {raceTypeIconMap[row.raceTypeId] && (
                                                <Tooltip title={t(raceTypeIconMap[row.raceTypeId].labelKey)}>
                                                    {raceTypeIconMap[row.raceTypeId].icon}
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                            {row.status === 0 && (
                                                <Tooltip title={t('eventsetup.start')}>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => setConfirmAction({ type: 'start', eventId: row.raceId, eventName: row.name })}
                                                    >
                                                        <PlayArrowIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {row.status === 1 && (
                                                <Tooltip title={t('eventsetup.stop')}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => setConfirmAction({ type: 'stop', eventId: row.raceId, eventName: row.name })}
                                                    >
                                                        <StopIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>

                                    </TableRow>
                                );
                            })}
                        {emptyRows > 0 && (
                            <TableRow style={{ height: 33 * emptyRows }}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                rowsPerPageOptions={[25, 50, 100, 250, { value: -1, label: 'All' }]}
                count={orgEvents.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}

export default OrganizerEventsTable;